import type { DetectOverflowOptions } from '@floating-ui/react-dom';
import { arrow, autoUpdate, shift, useFloating } from '@floating-ui/react-dom';
import type { HTMLAttributes, ReactNode } from 'react';
import React, {
  useCallback,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useEffectEvent } from 'use-effect-event';
import { SCREEN_EDGE_PADDING } from './ContentPreviewPopOver';
import FloatingArrow from './FloatingArrow';
import * as styles from './HoverPreviewable.module.scss';
import contentPreviewPopOverSize from '../utils/popover/contentPreviewPopOverSize';
import useHover from '../utils/useHover';

const PRELOAD_DURATION = 200;

const DETECT_OVERFLOW_OPTIONS: Partial<DetectOverflowOptions> = {
  padding: SCREEN_EDGE_PADDING,
};

interface HoverPreviewableProps {
  children: (
    props: Pick<HTMLAttributes<HTMLElement>, 'onMouseEnter' | 'onMouseLeave'>,
  ) => React.ReactNode;
  renderPreview: (args: {
    isOpen: boolean;
    updatePopOver: () => void;
  }) => ReactNode;
  alignToTop?: boolean;
  forcePreviewOpen?: boolean;
}

const HoverPreviewable = ({
  children,
  renderPreview,
  alignToTop,
  forcePreviewOpen,
}: HoverPreviewableProps) => {
  const arrowRef = useRef<SVGSVGElement | null>(null);
  const middleware = useMemo(
    () => [
      shift(DETECT_OVERFLOW_OPTIONS),
      contentPreviewPopOverSize(DETECT_OVERFLOW_OPTIONS),
      arrow({ element: arrowRef, padding: 12 }),
    ],
    [],
  );

  const {
    refs,
    floatingStyles,
    update,
    placement: actualPlacement,
    middlewareData: { arrow: arrowData },
  } = useFloating({
    placement: alignToTop ? 'right-start' : 'right',
    middleware,
    whileElementsMounted: (...args) =>
      autoUpdate(...args, { ancestorScroll: false }),
  });

  const [isHovered, setIsHovered] = useState(false);
  const id = useId();

  const {
    onMouseEnter: originalOnMouseEnter,
    onMouseLeave: originalOnMouseLeave,
  } = useHover({
    hoverGroupId: 'hoverPreviewable',
    hoverDelay: 'slow',
    onHoverChange: (hoveringId) => {
      setIsHovered(hoveringId === id);
    },
  });

  const [shouldPreload, setShouldPreload] = useState(false);

  const shouldPreloadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const onMouseEnter = useCallback(
    (event: React.MouseEvent) => {
      originalOnMouseEnter(id, event);
      shouldPreloadTimeoutRef.current = setTimeout(() => {
        setShouldPreload(true);
      }, PRELOAD_DURATION);
    },
    [originalOnMouseEnter, id],
  );
  const onMouseLeave = useCallback(() => {
    originalOnMouseLeave(id);
    if (shouldPreloadTimeoutRef.current) {
      clearTimeout(shouldPreloadTimeoutRef.current);
      shouldPreloadTimeoutRef.current = null;
    }
  }, [originalOnMouseLeave, id]);

  const shouldShow = isHovered || !!forcePreviewOpen;
  const shouldRender = shouldShow || shouldPreload;

  const onHandleUpdate = useEffectEvent(() => update());
  useLayoutEffect(() => {
    if (shouldShow) onHandleUpdate();
  }, [shouldShow]);

  return (
    <div className={styles.container}>
      {children({ onMouseEnter, onMouseLeave })}
      <div ref={refs.setReference} className={styles.reference} />
      <div
        ref={refs.setFloating}
        style={{
          ...floatingStyles,
          ...(shouldShow ? {} : { visibility: 'hidden' }),
        }}
        aria-hidden={!shouldShow}
        className={styles.previewContainer}
      >
        {shouldRender && (
          <>
            <div
              className={styles.preview}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
            >
              {renderPreview({ isOpen: shouldShow, updatePopOver: update })}
            </div>
            <div className={styles.arrowContainer}>
              <FloatingArrow
                ref={arrowRef}
                placement={actualPlacement}
                arrowData={arrowData}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default HoverPreviewable;
